import { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Card, Chip, Avatar, List, ListItemButton,
  ListItemAvatar, ListItemText, CircularProgress, Divider,
} from '@mui/material';
import {
  School, EventNote, MenuBook, TrendingUp, Description, AttachMoney,
  ArrowForwardIos, Groups, Message as MessageIcon, ChevronRight,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { fmtMoney, locale } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtKES = (v) => fmtMoney(v);

export default function ParentDashboard() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const school = JSON.parse(localStorage.getItem('school') || '{}');
  const [summary, setSummary] = useState({ children: [], totals: {}, upcomingEvents: 0 });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [sRes, eRes] = await Promise.all([
          axios.get(`${API}/api/guardians/me/summary`, { headers: hdrs() }),
          axios.get(`${API}/api/academics/events`, { headers: hdrs() }).catch(() => ({})),
        ]);
        setSummary(sRes.data || { children: [], totals: {} });
        setEvents(eRes.data?.data || []);
      } catch (e) {
        console.error('Load error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const { children, totals } = summary;
  const balance = totals?.balance || 0;

  // Menu items with live summaries aggregated across ALL the parent's children
  const MENU = [
    {
      icon: <AttachMoney />, color: '#e65100', label: 'School Fees', to: '/parent/fees',
      summary: balance > 0 ? `${fmtKES(balance)} outstanding` : 'All fees cleared',
      warn: balance > 0,
    },
    {
      icon: <Description />, color: '#1565c0', label: 'Fee Statements', to: '/parent/statements',
      summary: `Billed ${fmtKES(totals?.billed)} • Paid ${fmtKES(totals?.paid)}`,
    },
    {
      icon: <TrendingUp />, color: '#2e7d32', label: 'Results', to: '/parent/assessments',
      summary: totals?.results ? `${totals.results} assessment result${totals.results !== 1 ? 's' : ''}` : 'No results published yet',
    },
    {
      icon: <MenuBook />, color: '#6a1b9a', label: 'Academics', to: '/parent/academics',
      summary: 'Subjects and learning areas',
    },
    {
      icon: <Groups />, color: '#00695c', label: 'Class Teachers', to: '/parent/teachers',
      summary: 'Contact your children\'s teachers',
    },
    {
      icon: <MessageIcon />, color: '#283593', label: 'Messages', to: '/parent/messages',
      summary: 'School communication',
    },
    {
      icon: <EventNote />, color: '#ad1457', label: 'School Events', to: null, // scrolls below
      summary: summary.upcomingEvents ? `${summary.upcomingEvents} upcoming` : 'Nothing scheduled',
      onClick: () => document.getElementById('parent-events')?.scrollIntoView({ behavior: 'smooth' }),
    },
  ];

  if (loading) return <Box textAlign="center" py={10}><CircularProgress /></Box>;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8', pb: 6 }}>
      {/* Compact hero */}
      <Box sx={{
        color: 'white', pt: 3, pb: 4, px: 2, mb: -3, borderRadius: '0 0 24px 24px',
        backgroundImage: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
      }}>
        <Container maxWidth="sm" disableGutters sx={{ px: 1 }}>
          <Typography variant="h5" fontWeight={800}>
            Hi, {user.fullName?.split(' ')[0] || 'there'} 👋
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {school.name || 'Parent Portal'}
          </Typography>
          {/* Balance banner — the number parents care about most */}
          <Card sx={{ mt: 2, p: 1.5, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}
            onClick={() => navigate('/parent/fees')}>
            <Avatar sx={{ bgcolor: balance > 0 ? '#fff3e0' : '#e8f5e9', color: balance > 0 ? '#e65100' : '#2e7d32' }}>
              <AttachMoney />
            </Avatar>
            <Box flex={1}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                FEE BALANCE {children.length > 1 ? `— ${children.length} CHILDREN` : ''}
              </Typography>
              <Typography variant="h6" fontWeight={800} lineHeight={1.1}
                color={balance > 0 ? 'warning.dark' : 'success.main'}>
                {fmtKES(balance)}
              </Typography>
            </Box>
            <ArrowForwardIos sx={{ fontSize: 14, color: 'text.disabled' }} />
          </Card>
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ pt: 5 }}>
        {/* Children */}
        <Typography variant="subtitle1" fontWeight={800} mb={1}>My Children</Typography>
        {children.length === 0 ? (
          <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3, mb: 3 }}>
            <School sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary" variant="body2">
              No learners linked to your account yet. Ask the school to grant your
              email portal access on your child's guardian record.
            </Typography>
          </Card>
        ) : (
          <Box mb={3} display="flex" flexDirection="column" gap={1}>
            {children.map(c => (
              <Card key={c.admNo} sx={{ borderRadius: 3 }}>
                <ListItemButton sx={{ py: 1.5, borderRadius: 3 }}
                  onClick={() => navigate(`/parent/learners/${c.admNo}`)}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main', fontWeight: 700 }}>
                      {(c.name || '?').charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography fontWeight={700}>{c.name}</Typography>}
                    secondary={`${c.stage || '—'} • Adm ${c.admNo}${c.relationship ? ` • your ${c.relationship.toLowerCase() === 'other' ? 'dependant' : c.relationship}` : ''}`}
                  />
                  <Box textAlign="right" mr={0.5}>
                    <Chip size="small"
                      label={c.feeBalance > 0 ? fmtKES(c.feeBalance) : 'Cleared'}
                      color={c.feeBalance > 0 ? 'warning' : 'success'}
                      sx={{ fontWeight: 700 }} />
                  </Box>
                  <ChevronRight sx={{ color: 'text.disabled' }} />
                </ListItemButton>
              </Card>
            ))}
          </Box>
        )}

        {/* Menu — every row is a link with a live summary across all children */}
        <Typography variant="subtitle1" fontWeight={800} mb={1}>Menu</Typography>
        <Card sx={{ borderRadius: 3, mb: 3 }}>
          <List disablePadding>
            {MENU.map((m, i) => (
              <Box key={m.label}>
                {i > 0 && <Divider component="li" />}
                <ListItemButton sx={{ py: 1.5 }}
                  onClick={() => (m.onClick ? m.onClick() : navigate(m.to))}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: `${m.color}18`, color: m.color }}>{m.icon}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography fontWeight={700}>{m.label}</Typography>}
                    secondary={
                      <Typography variant="caption"
                        color={m.warn ? 'warning.dark' : 'text.secondary'}
                        fontWeight={m.warn ? 700 : 400}>
                        {m.summary}
                      </Typography>
                    }
                  />
                  <ChevronRight sx={{ color: 'text.disabled' }} />
                </ListItemButton>
              </Box>
            ))}
          </List>
        </Card>

        {/* Upcoming events */}
        <Typography variant="subtitle1" fontWeight={800} mb={1} id="parent-events">
          📢 Upcoming Events
        </Typography>
        <Card sx={{ borderRadius: 3 }}>
          {events.length === 0 ? (
            <Box p={3} textAlign="center">
              <EventNote sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary" variant="body2">No events scheduled</Typography>
            </Box>
          ) : (
            events.slice(0, 8).map((evt, i) => {
              const dateStr = evt.date
                ? new Date(evt.date).toLocaleDateString(locale(), { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
                : '';
              const endStr = evt.endDate
                ? ` – ${new Date(evt.endDate).toLocaleDateString(locale(), { day: '2-digit', month: 'short' })}`
                : '';
              return (
                <Box key={evt._id || i} p={2}
                  borderBottom={i < Math.min(events.length, 8) - 1 ? '1px solid #eee' : 'none'}
                  sx={evt.status === 'Cancelled' ? { opacity: 0.6 } : undefined}>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="subtitle2" fontWeight={700}
                      sx={evt.status === 'Cancelled' ? { textDecoration: 'line-through' } : undefined}>
                      {evt.title}
                    </Typography>
                    {evt.category && <Chip label={evt.category} size="small" variant="outlined" sx={{ height: 18, fontSize: 11 }} />}
                    {evt.status === 'Cancelled' && <Chip label="Cancelled" size="small" color="error" sx={{ height: 18, fontSize: 11 }} />}
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {dateStr}{endStr}
                    {evt.venue ? ` • ${evt.venue}` : ''}
                    {evt.charge > 0 ? ` • ${fmtKES(evt.charge)}` : ' • Free'}
                  </Typography>
                  {evt.description && (
                    <Typography variant="body2" mt={0.5}>{evt.description}</Typography>
                  )}
                </Box>
              );
            })
          )}
        </Card>
      </Container>
    </Box>
  );
}
